import { type AuthHeader, authHeader } from "@roman/shared/lib/auth/authHeader";
import { tokenStorage } from "@roman/shared/lib/auth/tokenStorage";
import romanApi from "@roman/shared/lib/net/romanApi";
import type { AuthState } from "@roman/shared/types/Auth";
import type { GoogleLoginCallback } from "@roman/shared/types/Google";
import type { WebUser } from "@roman/shared/types/WebUser";
import { isWebPlatform } from "@roman/shared/utils/platform";
import { AxiosError } from "axios";
import { mutate } from "swr";
import { create } from "zustand";
import type { Toast } from "../hooks/useHandleErrorBase";
import type { AppleLoginCallback } from "../types/Apple";
import { isEmailAlreadyUsedThumbaError } from "../utils/thumbaError";

export type AuthStoreState = {
  authState: AuthState;
  user: WebUser | null;
  isNewUser: boolean;
  setIsNewUser: (isNewUser: boolean) => void; // Currently, we cannot remove this function yet
};

export type EmailPasswordLoginCallback = (args: {
  onSuccess: (data: { accessToken: string; refreshToken?: string; isFirstTime: boolean }) => void;
  onError: (error: unknown) => void;
}) => Promise<void>;

export type AuthStoreActions = {
  loginWithGoogle: (trigger: GoogleLoginCallback, toast: Toast) => Promise<void>;
  loginWithApple: (trigger: AppleLoginCallback, toast: Toast) => Promise<void>;
  loginWithEmailPassword: (trigger: EmailPasswordLoginCallback, toast: Toast) => Promise<void>;
  refreshToken: (onSuccess?: () => void, onFailure?: () => void) => Promise<void>;
  fetchAndSetUser: () => Promise<void>;
  logout: (toast: Toast, navigateToLogin: () => void, clearCookies?: () => void) => Promise<void>;
  deleteMe: (headers: AuthHeader, toast: Toast) => Promise<void>;
  refetch: () => Promise<void>;
  loadPersistedUser: () => Promise<void>;
};

export const useAuthStore = create<AuthStoreState & AuthStoreActions>()((set, get) => {
  const _setAuthState = (state: AuthState) => set({ authState: state });
  const _setUser = async (user: WebUser) => {
    set({ user });
    // Persist user data to storage (mobile only)
    if (!isWebPlatform()) {
      await tokenStorage.setUser(JSON.stringify(user));
    }
  };
  const _clearUser = async () => {
    set({ user: null, authState: "unauthenticated" });
    await authHeader.clear();

    if (isWebPlatform()) {
      localStorage.removeItem("loginAt");
      // Ensure user data is removed from localStorage if it exists
      localStorage.removeItem("auth_user");
    } else {
      // Clear all tokens on mobile
      await tokenStorage.clearAll();
    }
  };
  const _setTokenAndLoginAt = async (token: string, refreshToken?: string) => {
    await authHeader.set(token);

    // Save refresh token for mobile platforms
    if (!isWebPlatform() && refreshToken) {
      await tokenStorage.setRefreshToken(refreshToken);
    }

    if (isWebPlatform()) {
      localStorage.setItem("loginAt", JSON.stringify({ loginAt: new Date().toISOString() }));
    } else {
      await tokenStorage.setLoginAt(new Date().toISOString());
    }
  };

  const _fetchUser = async () => {
    const headers = authHeader.getOrThrow();
    const response = await romanApi.get<{ user: WebUser }>("/user/me", undefined, { headers });
    return response.user;
  };

  const getErrorMessage = (error: unknown): string => {
    if (error instanceof AxiosError && isEmailAlreadyUsedThumbaError(error.response?.data.body)) {
      return "このメールアドレスはすでに別のアカウントで登録されています。";
    }
    return "ログインに失敗しました";
  };

  return {
    authState: "unknown",
    user: null,
    isNewUser: false,
    setIsNewUser: (isNewUser: boolean) => set({ isNewUser }),

    loginWithGoogle: async (trigger: GoogleLoginCallback, toast: Toast) => {
      const { setIsNewUser, fetchAndSetUser, authState } = get();

      if (authState === "loggingIn") {
        return;
      }

      _setAuthState("loggingIn");

      await trigger({
        onSuccess: async (data) => {
          await _setTokenAndLoginAt(data.accessToken, data.refreshToken);
          setIsNewUser(data.isFirstTime);
          await fetchAndSetUser();
        },
        onError: async (error) => {
          await _clearUser();
          const message = getErrorMessage(error);
          toast.error(message);
        },
      });
    },

    loginWithApple: async (trigger: AppleLoginCallback, toast: Toast) => {
      const { setIsNewUser, fetchAndSetUser, authState } = get();

      if (authState === "loggingIn") {
        return;
      }

      _setAuthState("loggingIn");

      await trigger({
        onSuccess: async (data) => {
          await _setTokenAndLoginAt(data.accessToken, data.refreshToken);
          setIsNewUser(data.isFirstTime);
          await fetchAndSetUser();
        },
        onError: async () => {
          await _clearUser();
          toast.error("ログインに失敗しました");
        },
      });
    },

    loginWithEmailPassword: async (trigger: EmailPasswordLoginCallback, toast: Toast) => {
      const { setIsNewUser, fetchAndSetUser, authState } = get();

      if (authState === "loggingIn") {
        return;
      }

      _setAuthState("loggingIn");

      await trigger({
        onSuccess: async (data) => {
          await _setTokenAndLoginAt(data.accessToken, data.refreshToken);
          setIsNewUser(data.isFirstTime);
          await fetchAndSetUser();
        },
        onError: async (error) => {
          await _clearUser();
          const message = getErrorMessage(error);
          toast.error(message);
        },
      });
    },

    fetchAndSetUser: async () => {
      _setAuthState("fetchingUser");
      try {
        const user = await _fetchUser();
        await _setUser(user);
        _setAuthState("authenticated");
      } catch (error) {
        _setAuthState("unauthenticated");
        await _clearUser();
      }
    },

    refreshToken: async (onSuccess?: () => void, onFailure?: () => void) => {
      try {
        let response: { accessToken: string; refreshToken?: string };
        const isWeb = isWebPlatform();

        if (isWeb) {
          // Web: Use httpOnly cookie for refresh token
          response = await romanApi.post<{ accessToken: string }, void>("/auth/refresh", undefined, {
            withCredentials: true,
          });
        } else {
          // Mobile: Send refresh token in request body to mobile endpoint (no cookies)
          const storedRefreshToken = await tokenStorage.getRefreshToken();
          response = await romanApi.post<{ accessToken: string; refreshToken?: string }, { refreshToken: string }>(
            "/auth/refresh/mobile",
            { refreshToken: storedRefreshToken || "" },
          );
        }

        await _setTokenAndLoginAt(response.accessToken, response.refreshToken);
        onSuccess?.();
        await get().fetchAndSetUser();
      } catch (error) {
        await _clearUser();
        onFailure?.();
      }
    },

    logout: async (toast: Toast, navigateToLogin: () => void, clearCookies?: () => void) => {
      if (get().authState === "loggingOut") {
        return;
      }

      _setAuthState("loggingOut");

      try {
        if (isWebPlatform()) {
          await romanApi.delete("/auth/logout", { withCredentials: true });
        } else {
          clearCookies?.();
        }

        await _clearUser();
        const shouldRevalidate = !isWebPlatform();
        mutate(() => true, undefined, { revalidate: shouldRevalidate });

        navigateToLogin();
      } catch (error) {
        toast.error("ログアウトに失敗しました");
      }
    },

    deleteMe: async (headers: AuthHeader, toast: Toast) => {
      if (get().authState === "loggingOut") {
        return;
      }
      _setAuthState("loggingOut");
      try {
        await romanApi.post("/user-delete-requests", {}, { headers, withCredentials: true });
        await _clearUser();
        mutate(() => true, undefined, { revalidate: false });
      } catch (error) {
        toast.error("ユーザーの削除に失敗しました");
      }
    },

    refetch: async () => {
      const user = await _fetchUser();
      await _setUser(user);
    },

    loadPersistedUser: async () => {
      try {
        // Web: No persistence - user data is not stored in localStorage
        // Always start as unauthenticated and rely on refresh token cookie
        if (isWebPlatform()) {
          set({ authState: "unauthenticated" });
          return;
        }

        // Mobile: Load persisted user data from secure storage
        const userJson = await tokenStorage.getUser();
        if (userJson) {
          const user = JSON.parse(userJson) as WebUser;
          set({ user, authState: "authenticated" });
        } else {
          // No persisted user found, set to unauthenticated
          set({ authState: "unauthenticated" });
        }
      } catch (error) {
        console.error("Failed to load persisted user:", error);
        set({ authState: "unauthenticated" });
      }
    },
  };
});

export const isUnknownAuthState = (authState: AuthState) => {
  return authState === "unknown" || authState === "refreshingToken" || authState === "fetchingUser";
};

export const tryGetAuthenticatedUser = (): WebUser | null => useAuthStore((state) => state.user);

export const getAuthenticatedUser = (): WebUser => {
  const user = tryGetAuthenticatedUser();

  if (!user) {
    throw new Error("You must use this function inside a protected route");
  }

  return user;
};
